import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";
import { namedInteractions } from "../../app/namedInteractions";

import { toMessage } from "../../api/errors";
import type { EventResponse } from "../../api/schemas/events";
import { i18next } from "../../i18n";
import { provenanceView } from "../../widgets/provenance";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

/** v1 `formatEventData`: "no data" / pretty JSON / `String()` / "invalid data" on throw. */
export const formatEventData = (data: unknown): string => {
	if (data === null || data === undefined) {
		return i18next.t("audit.noData");
	}
	try {
		return typeof data === "object"
			? JSON.stringify(data, null, 2)
			: String(data);
	} catch {
		return i18next.t("audit.invalidData");
	}
};

/** v1 `formatDateTime`: `toLocaleString`, raw string if parsing throws. */
export const formatDateTime = (datetime: string): string => {
	try {
		return new Date(datetime).toLocaleString();
	} catch {
		return datetime;
	}
};

/** v1 `getEventTypeColor`: first substring match wins. */
export const eventTypeColor = (type: string): string => {
	if (type.includes("create")) return "badge-success";
	if (type.includes("update")) return "badge-info";
	if (type.includes("delete")) return "badge-error";
	return "badge-ghost";
};

const eventRow = (h: HtmlBuilder<Message>, event: EventResponse): Html =>
	h.div(
		[h.Key(event.id), h.Class("p-6"), h.DataAttribute("testid", "audit-event")],
		[
			h.div(
				[h.Class("flex items-start justify-between")],
				[
					h.div(
						[h.Class("flex-1")],
						[
							h.div(
								[h.Class("flex items-center gap-3 mb-2")],
								[
									h.span(
										[h.Class(`badge badge-soft ${eventTypeColor(event.type)}`)],
										[event.type],
									),
									h.span(
										[h.Class("text-sm text-base-content/60")],
										[event.source],
									),
									// R19: who made the write, with the popover for agent writes.
									provenanceView(h, event.provenance),
									h.span(
										[h.Class("text-sm text-base-content/50")],
										[`v${event.version}`],
									),
									h.span(
										[h.Class("text-sm text-base-content/70")],
										[formatDateTime(event.datetime)],
									),
								],
							),
							event.data === null || event.data === undefined
								? h.empty
								: h.details(
										[h.Class("mt-3")],
										[
											h.summary(
												[
													h.DataAttribute(
														"analytics-event",
														namedInteractions.auditEventDetails,
													),
													h.Class(
														"cursor-pointer text-sm font-medium text-base-content/80 hover:text-base-content",
													),
												],
												[i18next.t("audit.viewEventData")],
											),
											h.pre(
												[
													h.Class(
														"mt-2 p-3 bg-base-200 rounded text-xs overflow-x-auto",
													),
												],
												[formatEventData(event.data)],
											),
										],
									),
						],
					),
					h.div(
						[h.Class("text-xs text-base-content/50 ml-4")],
						[`ID: ${event.id}`],
					),
				],
			),
		],
	);

const body = (h: HtmlBuilder<Message>, model: Model): Html =>
	AsyncData.match(model.events, {
		onIdle: () => h.empty,
		onLoading: () =>
			h.div(
				[h.Role("status"), h.Class("p-6 text-center")],
				[
					h.div([h.Class("loading loading-spinner loading-lg mx-auto")], []),
					h.p(
						[h.Class("mt-2 text-base-content/70")],
						[i18next.t("audit.loading")],
					),
				],
			),
		onFailure: (error) =>
			h.div(
				[h.Role("alert"), h.Class("p-6 text-center text-error")],
				[h.p([], [i18next.t("audit.error", { message: toMessage(error) })])],
			),
		onRefreshing: (events) =>
			list(h, model, events.data, events.nextCursor._tag === "Some"),
		onStale: ({ data }) =>
			list(h, model, data.data, data.nextCursor._tag === "Some"),
		onSuccess: (events) =>
			list(h, model, events.data, events.nextCursor._tag === "Some"),
	});

const list = (
	h: HtmlBuilder<Message>,
	model: Model,
	events: ReadonlyArray<EventResponse>,
	hasNextPage: boolean,
): Html =>
	events.length === 0
		? h.div(
				[h.Class("p-6 text-center text-base-content/70")],
				[h.p([], [i18next.t("audit.noEvents")])],
			)
		: h.div(
				[h.Class("divide-y divide-base-300")],
				[
					...events.map((event) => eventRow(h, event)),
					hasNextPage
						? h.div(
								[h.Class("p-6 text-center border-t")],
								[
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-primary"),
											h.AriaLabel(i18next.t("audit.loadMore")),
											h.Disabled(model.loadingMore),
											h.OnClick(Message.ClickedLoadMore()),
										],
										[
											model.loadingMore
												? i18next.t("audit.loadingMore")
												: i18next.t("audit.loadMore"),
										],
									),
								],
							)
						: h.empty,
				],
			);

export const view = defineView<Model, Message, Shared>((model, _shared, h) =>
	h.div(
		[
			h.DataAttribute("testid", "page-AuditLog"),
			h.Class("container mx-auto px-4 py-8"),
		],
		[
			h.div(
				[h.Class("mb-6")],
				[
					h.h1(
						[h.Class("text-3xl font-bold text-base-content mb-4")],
						[i18next.t("audit.title")],
					),
					h.div(
						[h.Class("mb-4")],
						[
							h.input([
								h.Type("text"),
								h.Placeholder(i18next.t("audit.searchPlaceholder")),
								h.AriaLabel(i18next.t("audit.searchPlaceholder")),
								h.Value(model.search),
								h.OnChange((search) => Message.ChangedSearch({ search })),
								h.Class("input w-full max-w-md"),
							]),
						],
					),
				],
			),
			h.div([h.Class("bg-base-100 shadow rounded-lg")], [body(h, model)]),
		],
	),
);
